import { useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useStream } from '@langchain/langgraph-sdk/react';
import { Paper, makeStyles } from '@material-ui/core';
import MessagesPanel from './messagePanel';
import Composer from './composer';
import { prepareLevelingAgentConfig } from '../../../../utility/levelingAgent';
import {
  LEVELING_AGENT_ASSISTANT_ID,
  LEVELING_AGENT_URL,
} from '../../../../constants/appConfig';

const useStyles = makeStyles(theme => ({
  root: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    gap: theme.spacing(6),
    minHeight: 0,
    padding: theme.spacing(6, 6),
  },
  composer: {
    flexShrink: 0,
  },
}));

const Chat = ({
  className = undefined,
  translations,
  auth,
  isDisabled = false,
  onGetLevelingFrameworks,
}) => {
  const classes = useStyles();

  const { kadar_api_url, kadar_api_token } = useMemo(
    () => prepareLevelingAgentConfig({ kadarApiToken: auth?.access }),
    [auth?.access]
  );

  const defaultHeaders = useMemo(() => {
    if (!kadar_api_token || !kadar_api_url) {
      return undefined;
    }

    return {
      Authorization: `Bearer ${kadar_api_token}`,
      'X-Kadar-Api-Url': kadar_api_url,
    };
  }, [kadar_api_token, kadar_api_url]);

  const stream = useStream({
    apiUrl: LEVELING_AGENT_URL,
    assistantId: LEVELING_AGENT_ASSISTANT_ID,
    defaultHeaders,
    throttle: 50,
  });

  const { submit, stop, isLoading, error, values } = stream;
  const messages = useMemo(
    () => stream.messages,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- values is the stable useStream source
    [values]
  );
  const tools = useMemo(
    () => stream.toolCalls,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- values is the stable useStream source
    [values]
  );
  const processedToolCallIdsRef = useRef(new Set());

  const handleWriteToolSuccess = useCallback(
    ({ toolCallId }) => {
      if (!toolCallId || processedToolCallIdsRef.current.has(toolCallId)) {
        return;
      }

      processedToolCallIdsRef.current.add(toolCallId);
      onGetLevelingFrameworks();
    },
    [onGetLevelingFrameworks]
  );

  return (
    <Paper className={classNames(classes.root, className)} elevation={0}>
      <MessagesPanel
        translations={translations}
        auth={auth}
        messages={messages}
        tools={tools}
        error={error}
        isLoading={isLoading}
        onWriteToolSuccess={handleWriteToolSuccess}
      />
      <Composer
        className={classes.composer}
        translations={translations}
        kadarApiUrl={kadar_api_url}
        kadarApiToken={kadar_api_token}
        isLoading={isLoading}
        isDisabled={isDisabled}
        onStop={stop}
        onSubmit={submit}
      />
    </Paper>
  );
};

Chat.propTypes = {
  className: PropTypes.string,
  translations: PropTypes.object.isRequired,
  auth: PropTypes.shape({}).isRequired,
  isDisabled: PropTypes.bool,
  onGetLevelingFrameworks: PropTypes.func.isRequired,
};

export default Chat;
